import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Switch,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  useCreateWhiteLabelWebsiteMutation,
  useGetWhiteLabelWebsiteByCompanyQuery,
  useUpdateWhiteLabelWebsiteMutation,
} from "../../../redux/admin/whiteLabelApi";
import WhiteLabelWorkspaceShell from "../Components/WhiteLabelWorkspaceShell";
import {
  getApiErrorMessage,
  isSafeAssetReference,
  isValidTimezone,
  LANGUAGE_CODE_REGEX,
  mapValidationErrors,
  normalizeAssetReference,
  splitKeywords,
  toAssetPayload,
} from "../Utils/whiteLabelFormUtils";
import { toast } from "react-hot-toast";

const EMPTY_FORM = Object.freeze({
  title: "",
  description: "",
  keywordsText: "",
  language: "en",
  timezone: "UTC",
  landingPage: {
    heroTitle: "",
    heroSubtitle: "",
    heroButtonText: "",
    heroImage: { url: "", publicId: "" },
  },
  contact: {
    address: "",
    email: "",
    phone: "",
  },
  seo: {
    metaTitle: "",
    metaDescription: "",
    ogImage: { url: "", publicId: "" },
    favicon: { url: "", publicId: "" },
  },
  isPublished: false,
  maintenanceMode: false,
});

function normalizeWebsite(record) {
  if (!record) return EMPTY_FORM;

  return {
    title: record.title || "",
    description: record.description || "",
    keywordsText: Array.isArray(record.keywords) ? record.keywords.join(", ") : "",
    language: record.language || "en",
    timezone: record.timezone || "UTC",
    landingPage: {
      heroTitle: record.landingPage?.heroTitle || "",
      heroSubtitle: record.landingPage?.heroSubtitle || "",
      heroButtonText: record.landingPage?.heroButtonText || "",
      heroImage: normalizeAssetReference(record.landingPage?.heroImage),
    },
    contact: {
      address: record.contact?.address || "",
      email: record.contact?.email || "",
      phone: record.contact?.phone || "",
    },
    seo: {
      metaTitle: record.seo?.metaTitle || "",
      metaDescription: record.seo?.metaDescription || "",
      ogImage: normalizeAssetReference(record.seo?.ogImage),
      favicon: normalizeAssetReference(record.seo?.favicon),
    },
    isPublished: Boolean(record.isPublished),
    maintenanceMode: Boolean(record.maintenanceMode),
  };
}

function AssetFields({ label, value, onChange, urlError }) {
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 8 }}>
        <TextField
          fullWidth
          label={`${label} URL / Path`}
          value={value.url}
          onChange={(event) =>
            onChange({
              ...value,
              url: event.target.value,
              })
            }
          error={Boolean(urlError)}
          helperText={urlError || " "}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          label={`${label} Public ID`}
          value={value.publicId}
          onChange={(event) =>
            onChange({
              ...value,
              publicId: event.target.value,
            })
          }
        />
      </Grid>
    </Grid>
  );
}

function validateForm(form) {
  const errors = {};
  if (!String(form.title || "").trim()) {
    errors.title = "Title is required.";
  }
  if (form.language && !LANGUAGE_CODE_REGEX.test(form.language)) {
    errors.language = "Enter a valid language code.";
  }
  if (form.timezone && !isValidTimezone(form.timezone)) {
    errors.timezone = "Enter a valid IANA timezone.";
  }
  if (
    form.contact.email &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(form.contact.email).trim())
  ) {
    errors["contact.email"] = "Contact email must be valid.";
  }
  [
    ["landingPage.heroImage", form.landingPage.heroImage],
    ["seo.ogImage", form.seo.ogImage],
    ["seo.favicon", form.seo.favicon],
  ].forEach(([field, value]) => {
    if (value?.url && !isSafeAssetReference(value.url)) {
      errors[`${field}.url`] = "Enter a valid URL or safe file reference.";
    }
  });
  return errors;
}

function buildPayload(companyId, form) {
  return {
    companyId,
    title: String(form.title || "").trim(),
    description: String(form.description || "").trim() || undefined,
    keywords: splitKeywords(form.keywordsText),
    language: String(form.language || "").trim() || undefined,
    timezone: String(form.timezone || "").trim() || undefined,
    landingPage: {
      heroTitle: String(form.landingPage.heroTitle || "").trim() || undefined,
      heroSubtitle:
        String(form.landingPage.heroSubtitle || "").trim() || undefined,
      heroButtonText:
        String(form.landingPage.heroButtonText || "").trim() || undefined,
      heroImage: toAssetPayload(form.landingPage.heroImage),
    },
    contact: {
      address: String(form.contact.address || "").trim() || undefined,
      email: String(form.contact.email || "").trim() || undefined,
      phone: String(form.contact.phone || "").trim() || undefined,
    },
    seo: {
      metaTitle: String(form.seo.metaTitle || "").trim() || undefined,
      metaDescription:
        String(form.seo.metaDescription || "").trim() || undefined,
      ogImage: toAssetPayload(form.seo.ogImage),
      favicon: toAssetPayload(form.seo.favicon),
    },
    isPublished: Boolean(form.isPublished),
    maintenanceMode: Boolean(form.maintenanceMode),
  };
}

export default function WhiteLabelWebsitePage() {
  const theme = useTheme();
  const [companyId, setCompanyId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const {
    data: website,
    isFetching,
    error,
    refetch,
  } = useGetWhiteLabelWebsiteByCompanyQuery(companyId, {
    skip: !companyId,
  });

  const [createWebsite, { isLoading: isCreating }] =
    useCreateWhiteLabelWebsiteMutation();
  const [updateWebsite, { isLoading: isUpdating }] =
    useUpdateWhiteLabelWebsiteMutation();

  useEffect(() => {
    if (website) {
      setForm(normalizeWebsite(website));
      setFieldErrors({});
      setSubmitError("");
    }
  }, [website]);

  useEffect(() => {
    if (!companyId) {
      setForm(EMPTY_FORM);
      setFieldErrors({});
      setSubmitError("");
    }
  }, [companyId]);

  const isMissingRecord = error?.status === 404;
  const errorMessage =
    error && !isMissingRecord
      ? getApiErrorMessage(error, "Failed to load website configuration.")
      : "";

  const handleSubmit = async () => {
    const nextErrors = validateForm(form);
    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      return;
    }

    try {
      const payload = buildPayload(companyId, form);
      if (isMissingRecord) {
        await createWebsite(payload).unwrap();
        toast.success("Website configuration created successfully.");
      } else {
        await updateWebsite(payload).unwrap();
        toast.success("Website configuration updated successfully.");
      }
      setSubmitError("");
      refetch();
    } catch (submitErrorValue) {
      setFieldErrors((prev) => ({
        ...prev,
        ...mapValidationErrors(submitErrorValue),
      }));
      const message = getApiErrorMessage(
        submitErrorValue,
        "Unable to save website configuration."
      );
      setSubmitError(message);
      toast.error(message);
    }
  };

  return (
    <WhiteLabelWorkspaceShell
      title="Website Manager"
      description="Load, create, and update company-specific website configuration using the existing backend contract."
      companyId={companyId}
      onCompanyChange={setCompanyId}
      error={errorMessage}
      actions={
        <Chip
          label={isMissingRecord ? "Website Not Created" : "Backend Connected"}
          color={isMissingRecord ? "warning" : "success"}
          variant={isMissingRecord ? "outlined" : "filled"}
          sx={{ fontWeight: 800 }}
        />
      }
    >
      {!companyId ? (
        <Alert severity="info">Select a company to manage website settings.</Alert>
      ) : isFetching ? (
        <Alert severity="info">Loading website configuration...</Alert>
      ) : (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, xl: 8 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: { xs: 2, md: 2.5 },
                border: `1px solid ${alpha(theme.palette.divider, 0.88)}`,
              }}
            >
              <Stack spacing={2}>
                {isMissingRecord ? (
                  <Alert severity="info">
                    No website configuration exists for this company yet. Saving will create it.
                  </Alert>
                ) : null}
                {submitError ? <Alert severity="error">{submitError}</Alert> : null}

                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  General
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      required
                      label="Title"
                      value={form.title}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, title: event.target.value }))
                      }
                      error={Boolean(fieldErrors.title)}
                      helperText={fieldErrors.title || " "}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Language"
                      value={form.language}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, language: event.target.value }))
                      }
                      error={Boolean(fieldErrors.language)}
                      helperText={fieldErrors.language || " "}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Timezone"
                      value={form.timezone}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, timezone: event.target.value }))
                      }
                      error={Boolean(fieldErrors.timezone)}
                      helperText={fieldErrors.timezone || " "}
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Description"
                  value={form.description}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, description: event.target.value }))
                  }
                />
                <TextField
                  fullWidth
                  label="Keywords"
                  placeholder="tracking, fleet, gps"
                  value={form.keywordsText}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, keywordsText: event.target.value }))
                  }
                />

                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Landing Page
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Hero Title"
                      value={form.landingPage.heroTitle}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          landingPage: {
                            ...prev.landingPage,
                            heroTitle: event.target.value,
                          },
                        }))
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Hero Button Text"
                      value={form.landingPage.heroButtonText}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          landingPage: {
                            ...prev.landingPage,
                            heroButtonText: event.target.value,
                          },
                        }))
                      }
                    />
                  </Grid>
                </Grid>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Hero Subtitle"
                  value={form.landingPage.heroSubtitle}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      landingPage: {
                        ...prev.landingPage,
                        heroSubtitle: event.target.value,
                      },
                    }))
                  }
                />
                <AssetFields
                  label="Hero Image"
                  value={form.landingPage.heroImage}
                  onChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      landingPage: {
                        ...prev.landingPage,
                        heroImage: value,
                      },
                    }))
                  }
                  urlError={fieldErrors["landingPage.heroImage.url"]}
                />

                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Contact
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Contact Email"
                      value={form.contact.email}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          contact: {
                            ...prev.contact,
                            email: event.target.value,
                          },
                        }))
                      }
                      error={Boolean(fieldErrors["contact.email"])}
                      helperText={fieldErrors["contact.email"] || " "}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Contact Phone"
                      value={form.contact.phone}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          contact: {
                            ...prev.contact,
                            phone: event.target.value,
                          },
                        }))
                      }
                    />
                  </Grid>
                </Grid>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Address"
                  value={form.contact.address}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      contact: {
                        ...prev.contact,
                        address: event.target.value,
                      },
                    }))
                  }
                />

                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  SEO
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Meta Title"
                      value={form.seo.metaTitle}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          seo: {
                            ...prev.seo,
                            metaTitle: event.target.value,
                          },
                        }))
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Meta Description"
                      value={form.seo.metaDescription}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          seo: {
                            ...prev.seo,
                            metaDescription: event.target.value,
                          },
                        }))
                      }
                    />
                  </Grid>
                </Grid>
                <AssetFields
                  label="OG Image"
                  value={form.seo.ogImage}
                  onChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      seo: {
                        ...prev.seo,
                        ogImage: value,
                      },
                    }))
                  }
                  urlError={fieldErrors["seo.ogImage.url"]}
                />
                <AssetFields
                  label="Favicon"
                  value={form.seo.favicon}
                  onChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      seo: {
                        ...prev.seo,
                        favicon: value,
                      },
                    }))
                  }
                  urlError={fieldErrors["seo.favicon.url"]}
                />

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.isPublished}
                          onChange={(event) =>
                            setForm((prev) => ({
                              ...prev,
                              isPublished: event.target.checked,
                            }))
                          }
                        />
                      }
                      label="Published"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.maintenanceMode}
                          onChange={(event) =>
                            setForm((prev) => ({
                              ...prev,
                              maintenanceMode: event.target.checked,
                            }))
                          }
                        />
                      }
                      label="Maintenance Mode"
                    />
                  </Grid>
                </Grid>

                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isCreating || isUpdating}
                  >
                    {isCreating || isUpdating
                      ? "Saving..."
                      : isMissingRecord
                        ? "Create Website"
                        : "Save Website"}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, xl: 4 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: 2.5,
                border: `1px solid ${alpha(theme.palette.divider, 0.88)}`,
              }}
            >
              <Stack spacing={1.25}>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {form.title || "Website Preview"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {form.description || "Description not configured."}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    label={form.isPublished ? "Published" : "Draft"}
                    color={form.isPublished ? "success" : "default"}
                    variant={form.isPublished ? "filled" : "outlined"}
                  />
                  <Chip
                    size="small"
                    label={form.maintenanceMode ? "Maintenance On" : "Maintenance Off"}
                    color={form.maintenanceMode ? "warning" : "default"}
                    variant={form.maintenanceMode ? "filled" : "outlined"}
                  />
                </Stack>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, pt: 1 }}>
                  Contact
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {form.contact.email || "No email"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {form.contact.phone || "No phone"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {form.contact.address || "No address"}
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
    </WhiteLabelWorkspaceShell>
  );
}
